// [claude-code 2026-03-20] S3-FIX:T4 — Walkthrough overhaul: contextual floating cards, 11 steps
import { useState, useEffect, useCallback, useRef } from "react";
import {
  hasPendingOnboardingTour,
  markOnboardingTourComplete,
} from "../../lib/onboarding-lifecycle";
import { detectOnboardingSurface } from "../../lib/onboarding-v2";
import { TourCard } from "./TourCard";
import { TOUR_STEPS, TOUR_STORAGE_KEY } from "./tour-content";

/* ------------------------------------------------------------------ */
/*  Tour orchestrator                                                  */
/* ------------------------------------------------------------------ */

export function FirstTimeTour({
  onNavigate,
}: {
  onNavigate?: (tab: string) => void;
}) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [opacity, setOpacity] = useState(0);
  const measureTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const surface = useRef(detectOnboardingSurface()).current;

  // Only runs when WelcomeOnboarding has just completed on this surface.
  useEffect(() => {
    if (!hasPendingOnboardingTour(surface)) return;
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    if (completed) {
      markOnboardingTourComplete(surface);
      return;
    }
    const timer = setTimeout(() => {
      setActive(true);
      onNavigate?.(TOUR_STEPS[0].nav!);
      requestAnimationFrame(() => setOpacity(1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [onNavigate, surface]);

  // Measure target element on step change
  useEffect(() => {
    if (!active) return;
    const current = TOUR_STEPS[step];
    if (!current) return;

    if (current.nav) onNavigate?.(current.nav);

    clearTimeout(measureTimer.current);
    measureTimer.current = setTimeout(() => {
      const el = document.querySelector(current.selector);
      setTargetRect(el ? el.getBoundingClientRect() : null);
    }, 250);

    return () => clearTimeout(measureTimer.current);
  }, [step, active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-measure on resize
  useEffect(() => {
    if (!active) return;
    const handle = () => {
      const el = document.querySelector(TOUR_STEPS[step]?.selector ?? "");
      if (el) setTargetRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, [active, step]);

  const completeTour = useCallback(() => {
    setOpacity(0);
    setTimeout(() => {
      markOnboardingTourComplete(surface);
      setActive(false);
      onNavigate?.("dashboard");
    }, 300);
  }, [onNavigate, surface]);

  const goNext = useCallback(() => {
    if (step < TOUR_STEPS.length - 1) setStep(step + 1);
    else completeTour();
  }, [step, completeTour]);

  const goPrev = useCallback(() => {
    if (step > 0) setStep(step - 1);
  }, [step]);

  if (!active) return null;

  return (
    <>
      {/* Dim overlay — semi-transparent, no blur */}
      <div
        className="fixed inset-0 z-[9998]"
        style={{
          background: "rgba(5, 4, 2, 0.56)",
          opacity,
          transition: "opacity 300ms ease-out",
        }}
        onClick={completeTour}
      />

      {/* Floating card */}
      <TourCard
        step={TOUR_STEPS[step]}
        stepIndex={step}
        totalSteps={TOUR_STEPS.length}
        targetRect={targetRect}
        onNext={goNext}
        onPrev={goPrev}
        onSkip={completeTour}
      />
    </>
  );
}
